// src/components/Timer.js

import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, StopCircle } from "lucide-react";
import { useTimer } from "../contexts/TimerContext";

export default function Timer({
  activityName,
  activityCategory,
  activityDate,
  isPremium = true,
  lang = "en",
  locales = {},
  onFinish,
  onCancel,
}) {
  const t = locales[lang] || {};
  const {
    timerState, setTimerState, startTimer,
  } = useTimer();

  const {
    seconds,
    running,
    paused,
    mode,      // "work" | "break"
    workDuration = 25,
    breakDuration = 5,
  } = timerState;

  const startedAtRef = useRef(null);
  const intervalRef = useRef(null);
  const pausedAtRef = useRef(null);      // when the current pause started
  const totalPausedMsRef = useRef(0);    // accumulated pause milliseconds

  // State สำหรับรอ user action (UI เท่านั้น)
  const [pendingBreak, setPendingBreak] = useState(false); // หลังจบ work
  const [pendingWork, setPendingWork] = useState(false);   // หลังจบ break

  // --- Countdown (sync context) ---
  useEffect(() => {
    if (running && !paused) {
      if (!startedAtRef.current) startedAtRef.current = new Date();
      intervalRef.current = setInterval(() => {
        setTimerState(prev => {
          if (prev.seconds <= 1) {
            clearInterval(intervalRef.current);

            // Pomodoro logic เฉพาะ UI
            if (prev.mode === "work") {
              setPendingBreak(true);
              setPendingWork(false);
              return { ...prev, running: false, seconds: 0 };
            } else {
              setPendingWork(true);
              setPendingBreak(false);
              return { ...prev, running: false, seconds: 0 };
            }
          }
          return { ...prev, seconds: prev.seconds - 1 };
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line
  }, [running, paused]);

  // --- Reset timer state ---
  const reset = () => {
    setTimerState((s) => ({
      ...s,
      running: false,
      paused: false,
      seconds: (s.workDuration || 25) * 60,
      mode: "work",
    }));
    startedAtRef.current = null;
    pausedAtRef.current = null;
    totalPausedMsRef.current = 0;
    setPendingBreak(false);
    setPendingWork(false);
    clearInterval(intervalRef.current);
  };

  // --- STOP timer พร้อม callback (บันทึก 1 session เท่านั้น) ---
  const handleStop = () => {
    clearInterval(intervalRef.current);
    const finishedAt = new Date();
    if (onFinish && startedAtRef.current) {
      // If currently paused, add current pause duration too
      const extraPausedMs = pausedAtRef.current ? finishedAt - pausedAtRef.current : 0;
      const netMs = (finishedAt - startedAtRef.current) - totalPausedMsRef.current - extraPausedMs;
      const duration = Math.max(0, Math.round(netMs / 1000));
      onFinish({
        startedAt: startedAtRef.current,
        finishedAt,
        duration_seconds: duration,
        duration: {
          hours: Math.floor(duration / 3600),
          minutes: Math.floor((duration % 3600) / 60),
          totalMinutes: Math.floor(duration / 60),
        },
        mode, // mode ที่หยุดตอนนั้น ("work" หรือ "break")
        activityName,
        activityCategory,
        activityDate,
      });
    }
    reset();
  };

  // --- CANCEL timer พร้อม callback (ไม่บันทึก) ---
  const handleCancel = () => {
    reset();
    if (onCancel) onCancel();
  };

  // --- Format mm:ss ---
  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // --- Pomodoro Settings UI (ไม่เกี่ยวกับ log จริง) ---
  const PomodoroSettings = () => (
    <div className="flex gap-4 items-center my-2">
      <label>
        {t.timer_work || "Work"}
        <input
          type="number"
          value={workDuration}
          min={1}
          max={120}
          className="mx-2 w-16 border rounded px-2 py-1"
          onChange={e =>
            setTimerState(s => ({
              ...s,
              workDuration: Number(e.target.value),
              seconds: Number(e.target.value) * 60,
            }))
          }
          disabled={running}
        />{t.timer_minute || "min"}
      </label>
      <label>
        {t.timer_break || "Break"}
        <input
          type="number"
          value={breakDuration}
          min={1}
          max={60}
          className="mx-2 w-16 border rounded px-2 py-1"
          onChange={e =>
            setTimerState(s => ({
              ...s,
              breakDuration: Number(e.target.value),
            }))
          }
          disabled={running}
        />{t.timer_minute || "min"}
      </label>
      <button
        className="ml-4 px-3 py-1 border rounded text-xs text-gray-600 bg-gray-100"
        type="button"
        disabled={running}
        onClick={() =>
          setTimerState(s => ({
            ...s,
            workDuration: 25,
            breakDuration: 5,
            seconds: 25 * 60,
          }))
        }
      >
        {t.timer_reset_default || "Reset"}
      </button>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-2xl px-7 py-9 flex flex-col items-center max-w-sm mx-auto mt-1 border border-indigo-100">
      {(activityName || activityCategory || activityDate) && (
        <div className="mb-4 text-center">
          {activityName && <div className="text-3xl font-semibold text-indigo-600">{activityName}</div>}
          {activityCategory && <span className="text-2xl text-pink-400 font-medium">{activityCategory}</span>}
          {activityDate && <div className="text-xs italic text-gray-400 mt-4">{activityDate}</div>}
        </div>
      )}
      <div className="text-3xl font-bold mb-4 text-indigo-800 tracking-wide">
        {mode === "work" ? t.timer_work_title || "⏳ Work" : t.timer_break_title || "🍵 Break"}
      </div>
      <div className={`text-7xl font-mono font-extrabold mb-4 ${mode === "break" ? "text-emerald-500" : "text-indigo-600"}`}>
        {formatTime(seconds)}
      </div>
      {isPremium && PomodoroSettings()}
      <div className="text-sm text-indigo-600 mb-2 text-center leading-snug">
        <span role="img" aria-label="tomato">🍅</span> <b>25/5 = 25 min focus + 5 min break</b>
        <br />
        Try <b>60/20</b> for deep work. <span className="text-emerald-600">Find your own rhythm!</span>
      </div>

      {/* ปุ่มหลังครบ work/break session (เฉพาะ UI) */}
      {pendingBreak ? (
        <div className="flex gap-4 mt-6">
          <button
            className="px-6 py-2 rounded-xl bg-emerald-500 text-white font-semibold"
            onClick={() => {
              setTimerState(s => ({
                ...s,
                mode: "break",
                seconds: (s.breakDuration || 5) * 60,
                running: true,
                paused: false,
              }));
              setPendingBreak(false);
            }}
          >
            {t.start_break || "Start Break"}
          </button>
          <button
            className="px-6 py-2 rounded-xl bg-gray-200 text-gray-700 font-semibold"
            onClick={() => {
              setTimerState(s => ({
                ...s,
                mode: "work",
                seconds: (s.workDuration || 25) * 60,
                running: true,
                paused: false,
              }));
              setPendingBreak(false);
            }}
          >
            {t.skip_break || "Skip Break"}
          </button>
        </div>
      ) : pendingWork ? (
        <div className="flex gap-4 mt-6">
          <button
            className="px-6 py-2 rounded-xl bg-indigo-500 text-white font-semibold"
            onClick={() => {
              setTimerState(s => ({
                ...s,
                mode: "work",
                seconds: (s.workDuration || 25) * 60,
                running: true,
                paused: false,
              }));
              setPendingWork(false);
            }}
          >
            {t.start_work || "Start Next Work"}
          </button>
          <button
            className="px-6 py-2 rounded-xl bg-gray-200 text-gray-700 font-semibold"
            onClick={reset}
          >
            {t.stop_pomodoro || "Stop"}
          </button>
        </div>
      ) : (
        <div className="flex gap-3 mt-4 w-full justify-center">
          {!running && (
            <>
              <button className="bg-gradient-to-r from-indigo-500 to-pink-400 text-white px-8 py-2 rounded-xl font-semibold shadow hover:scale-105 transition"
                onClick={() => {
                  if (!startedAtRef.current) startedAtRef.current = new Date();
                  startTimer({ activityName, activityCategory, activityDate });
                }}
              >
                <Play size={20} className="inline mr-1" /> {t.timer_start || "Start"}
              </button>
              <button className="bg-gray-100 text-gray-700 px-7 py-2 rounded-xl font-semibold shadow border border-gray-200 hover:bg-gray-200 transition"
                onClick={handleCancel} type="button"
              >
                {t.timer_cancel || "Cancel"}
              </button>
            </>
          )}
          {running && (
            <>
              <button className="bg-yellow-400 text-white px-7 py-2 rounded-xl font-semibold shadow hover:bg-yellow-500 transition"
                onClick={() => {
                  const now = new Date();
                  if (!paused) {
                    // Pausing: record when pause started
                    pausedAtRef.current = now;
                  } else {
                    // Resuming: accumulate paused duration
                    if (pausedAtRef.current) {
                      totalPausedMsRef.current += now - pausedAtRef.current;
                      pausedAtRef.current = null;
                    }
                  }
                  setTimerState(s => ({ ...s, paused: !s.paused }));
                }}
              >
                {paused
                  ? (<><Play size={18} className="inline mr-1" /> {t.timer_resume || "Resume"}</>)
                  : (<><Pause size={18} className="inline mr-1" /> {t.timer_pause || "Pause"}</>)
                }
              </button>
              <button className="bg-red-500 text-white px-7 py-2 rounded-xl font-semibold shadow hover:bg-red-600 transition"
                onClick={handleStop}
              >
                <StopCircle size={18} className="inline mr-1" /> {t.timer_stop || "Stop"}
              </button>
            </>
          )}
        </div>
      )}

      <div className="text-xs text-gray-400 mt-7">
        Powered by <span className="font-semibold text-indigo-500">Spendora Timer</span>
      </div>
    </div>
  );
}